import React, { useEffect, useState } from "react";
import { ListMusic, Save, RotateCcw, ChevronDown, Headphones } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TRACKS, ORIGINAL_TRACKS, resetTrack } from "@/data/playalong";
import { parseChord, keyPc } from "@/lib/playalongEngine";
import { loadTrackOverrides } from "@/lib/trackOverrides";
import { STYLES } from "@/data/playalongStyles";
import { PlayAlong } from "@/components/PlayAlong";
import { MelodyTapIn } from "@/components/MelodyTapIn";

const looseBars = (txt, n) => {
  const parts = txt.split("|");
  return Array.from({ length: n }, (_, i) => (parts[i] || "").trim().split(/\s+/).map((tok) => tok.match(/^([A-G][b#]?[2-6]|r):(\d*\.?\d+)$/)).filter(Boolean).map((m) => [m[1], Number(m[2])]));
};

const melToText = (m) => (m ? m.map((bar) => bar.map(([n, d]) => `${n}:${d}`).join(" ")).join(" | ") : "");
const parseMelody = (txt, beats) => {
  if (!txt.trim()) return { melody: null, errors: [] };
  const errors = [];
  const melody = txt.split("|").map((bar, i) => {
    const notes = bar.trim().split(/\s+/).filter(Boolean).map((tok) => {
      const m = tok.match(/^([A-G][b#]?[2-6]|r):(\d*\.?\d+)$/);
      if (!m) { errors.push(`bar ${i + 1}: "${tok}"`); return null; }
      return [m[1], Number(m[2])];
    }).filter(Boolean);
    const sum = notes.reduce((a, [, d]) => a + d, 0);
    if (!notes.length) errors.push(`bar ${i + 1} is empty`);
    else if (Math.abs(sum - beats) > 1e-6) errors.push(`bar ${i + 1} has ${sum} beats (needs ${beats})`);
    return notes;
  });
  return { melody, errors };
};

const KEYS = ["C", "Db", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"];

const TrackRow = ({ id, onSaved }) => {
  const t = TRACKS[id];
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: t.title, bpm: t.bpm, key: t.key, bars: t.bars.join(" | "), melody: melToText(t.melody) });
  const [listen, setListen] = useState(0);
  const style = STYLES[t.style];
  const beatsPerBar = style.steps / style.sub;
  const [busy, setBusy] = useState(false);
  const bars = f.bars.split("|").map((b) => b.trim()).filter(Boolean);
  const bad = bars.flatMap((b) => b.split(/\s+/)).filter((c) => !parseChord(c, 0));
  const melodyWarn = t.melody && bars.length !== ORIGINAL_TRACKS[id].bars.length;
  const mel = parseMelody(f.melody, beatsPerBar);
  const melErrors = [...mel.errors, ...(mel.melody && mel.melody.length !== bars.length ? [`melody has ${mel.melody.length} bars, chords have ${bars.length}`] : [])];
  const invalid = bad.length > 0 || melErrors.length > 0 || !bars.length;
  const form = () => ({ title: f.title, bpm: Number(f.bpm), key: f.key, bars, ...(mel.melody ? { melody: mel.melody } : {}) });
  const tryEdits = () => { if (!invalid) { Object.assign(TRACKS[id], form()); setListen((n) => n + 1); } };
  const preview = bad.length ? "" : bars.map((b) => b.split(/\s+/).map((c) => parseChord(c, keyPc(f.key)).label).join(" ")).join(" | ");

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/tracks/${id}`, form());
      Object.assign(TRACKS[id], form());
      toast.success("Track saved — students hear it straight away");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); } finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/tracks/${id}`);
      resetTrack(id);
      const o = TRACKS[id];
      setF({ title: o.title, bpm: o.bpm, key: o.key, bars: o.bars.join(" | "), melody: melToText(o.melody) });
      if (listen) setListen((n) => n + 1);
      toast.success("Reset to original");
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card" data-testid={`track-row-${id}`}>
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between px-5 py-3 text-left" data-testid={`track-open-${id}`}>
        <span><span className="font-medium text-foreground">{t.title}</span> <span className="text-xs text-muted-foreground">· {t.genre} · {t.key} · {t.bpm} bpm</span></span>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-3 border-t border-border px-5 py-4 text-sm">
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="sm:col-span-1">Title<input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid={`track-title-${id}`} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2" /></label>
            <label>Tempo (bpm)<input type="number" min={30} max={240} value={f.bpm} onChange={(e) => setF({ ...f, bpm: e.target.value })} data-testid={`track-bpm-${id}`} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2" /></label>
            <label>Key<select value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} data-testid={`track-key-${id}`} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2">{KEYS.map((k) => <option key={k}>{k}</option>)}</select></label>
          </div>
          <label className="block">Chords — one bar between each "|", Roman numerals (e.g. I | vi7 | ii7 V7 | I)
            <textarea rows={3} value={f.bars} onChange={(e) => setF({ ...f, bars: e.target.value })} data-testid={`track-bars-${id}`} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs" />
          </label>
          {bad.length > 0 ? <p className="text-xs text-red-500" data-testid={`track-error-${id}`}>Not recognised: {bad.join(", ")}</p>
            : <p className="font-mono text-xs text-primary" data-testid={`track-preview-${id}`}>{bars.length} bars: {preview}</p>}
          {t.song && (
            <label className="block">Melody — notes as NOTE:BEATS, bars separated by "|", "r" = rest ({beatsPerBar} beats per bar), e.g. D4:1 G4:2 | B4:0.5 G4:0.5 A4:2
              <textarea rows={4} value={f.melody} onChange={(e) => setF({ ...f, melody: e.target.value })} data-testid={`track-melody-${id}`} placeholder="Leave empty for chords only" className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-xs" />
              <span className="mt-2 block"><MelodyTapIn testId={`track-tapin-${id}`} bars={looseBars(f.melody, bars.length)} barCount={bars.length} beatsPerBar={beatsPerBar}
                setBars={(nb) => setF((cur) => ({ ...cur, melody: nb.map((b) => b.map(([n, d]) => `${n}:${d}`).join(" ")).join(" | ") }))} /></span>
              {melErrors.length > 0 && <span className="mt-1 block text-xs text-red-500" data-testid={`track-melody-error-${id}`}>{melErrors.slice(0, 4).join(" · ")}</span>}
            </label>
          )}
          {melodyWarn && !mel.melody && <p className="text-xs text-amber-500">This song has a melody line for {ORIGINAL_TRACKS[id].bars.length} bars — with a different number of bars the melody/score is hidden and the band plays chords only.</p>}
          <div className="flex gap-2">
            <button onClick={save} disabled={busy || invalid} data-testid={`track-save-${id}`} className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"><Save size={14} /> Save</button>
            <button onClick={reset} disabled={busy} data-testid={`track-reset-${id}`} className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm text-muted-foreground hover:text-foreground"><RotateCcw size={14} /> Reset to original</button>
            <button onClick={tryEdits} disabled={invalid} data-testid={`track-listen-${id}`} className="flex items-center gap-1.5 rounded-full border border-primary/50 px-4 py-2 text-sm text-primary hover:bg-primary/10 disabled:opacity-50"><Headphones size={14} /> {listen ? "Update preview" : "Listen"}</button>
          </div>
          {listen > 0 && <PlayAlong key={listen} entry={{ t: id, focus: "Preview — your unsaved edits are included. Press Save to publish them to students." }} instrumentId="guitarists" preview />}
        </div>
      )}
    </div>
  );
};

export const TrackEditor = () => {
  const [, refresh] = useState(0);
  const [ready, setReady] = useState(false);
  useEffect(() => { loadTrackOverrides(true).then(() => setReady(true)); }, []);
  return (
    <section className="mt-14" data-testid="admin-track-editor">
      <div className="flex items-center gap-2"><ListMusic size={18} className="text-primary" /><h2 className="font-serif text-2xl">Play-along tracks</h2></div>
      <p className="mt-1 text-sm text-muted-foreground">Fix the title, tempo, key or chords of any track. Changes apply to every student immediately.</p>
      <div className="mt-5 space-y-2">{ready && Object.keys(TRACKS).map((id) => <TrackRow key={id} id={id} onSaved={() => refresh((x) => x + 1)} />)}</div>
    </section>
  );
};
